import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
    BygrModule,
    CoreApi,
    CoreApiProvider,
    ILandingPageContact,
    LandingPageDetailsEdit,
    LoadingIndicator,
    SingleFilterDropDown,
    LandingPageSettings,
    LandingPageSetupImages,
    LandingPageSetupMessagesWrapper,
    LandingPageContactsEdit,
    FilterItem,
} from '@ygtechnologies/bygr-frontend-component-library';
import { Sticky, Tabs } from '@ygtechnologies/rubus-ui';
import Banner from '@/components/Banner';
import { ProjectPermissionToggle } from '@/components/permissions';
import React, { ReactNode, useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getConstructionPhases } from '@/services/construction-phase';
import { getResidentialUnitsByProjectId } from '@/services/residential-units';
import { IState } from '@/state/IState';
import { ConstructionPhase, Project } from '@/types';
import { useAsyncState } from '@/utils/hooks';
import theme from './LandingPageSetup.module.scss';
import LandingPageSetupMilestones from './LandingPageSetupMilestones';
import { useHistory, useLocation } from 'react-router';
import { OnChangeValue } from 'react-select';

interface LandingPageSetupProps {
    projectId: number;
}

export enum SetupTabId {
    Settings = 'settings',
    Details = 'details',
    Milestones = 'milestones',
    Images = 'images',
    Messages = 'messages',
    Contacts = 'contacts',
}
interface SetupTabDefinition {
    id: SetupTabId;
    caption(): ReactNode;
    render(props: { projectId: number }): ReactNode;
}

const setupTabDefinitions: SetupTabDefinition[] = [
    {
        id: SetupTabId.Settings,
        caption: () => <Trans id="landing_page_setup.settings_tab">Overview</Trans>,
        render: (props: { projectId: Project['id']; constructionPhaseId: ConstructionPhase['id']; module: BygrModule }) => (
            <LandingPageSettings {...props} bygrModule={BygrModule.ChangeManagement} />
        ),
    },
    {
        id: SetupTabId.Messages,
        caption: () => <Trans id="landing_page_setup.messages_tab">Messages</Trans>,
        render: (props: { projectId: Project['id']; constructionPhaseId: ConstructionPhase['id']; isAdmin: boolean }) => (
            <LandingPageSetupMessagesWrapper {...props} bygrModule={BygrModule.ChangeManagement} />
        ),
    },
    {
        id: SetupTabId.Milestones,
        caption: () => <Trans id="landing_page_setup.milestones_tab">Milestones</Trans>,
        render: (props: { projectId: Project['id']; constructionPhaseId: ConstructionPhase['id'] }) => (
            <LandingPageSetupMilestones {...props} />
        ),
    },
    {
        id: SetupTabId.Images,
        caption: () => <Trans id="landing_page_setup.images_tab">Images</Trans>,
        render: (props: { projectId: Project['id']; constructionPhaseId: ConstructionPhase['id'] }) => (
            <LandingPageSetupImages {...props} bygrModule={BygrModule.ChangeManagement} />
        ),
    },
    {
        id: SetupTabId.Details,
        caption: () => <Trans id="landing_page_setup.details_tab">Unit information</Trans>,
        render: (props: { projectId: Project['id']; constructionPhaseId: ConstructionPhase['id']; module: BygrModule }) => (
            <LandingPageDetailsEdit
                {...props}
                unitVariableUrl="settings/unit-variables"
                bygrModule={BygrModule.ChangeManagement}
            />
        ),
    },
    {
        id: SetupTabId.Contacts,
        caption: () => <Trans id="landing_page_setup.contacts_tab">Contacts</Trans>,
        render: (props: {
            projectId: Project['id'];
            constructionPhaseId: ConstructionPhase['id'];
            contacts: ILandingPageContact[];
        }) => <LandingPageContactsEdit {...props} bygrModule={BygrModule.ChangeManagement} />,
    },
];

type ConstructionPhaseFilterItem = FilterItem & {
    projectId: number | null;
};

const LandingPageSetup: React.FC<LandingPageSetupProps> = ({ projectId }) => {
    const history = useHistory();
    const location = useLocation();
    const [constructionPhases] = useAsyncState(() => getConstructionPhases(projectId), [projectId], null);
    const [selectedConstructionPhaseId, setSelectedConstructionPhaseId] = useState<number | null>(null);
    const [selectedSetupTabDefinition, setSelectedSetupTabDefinition] = useState<SetupTabDefinition>(setupTabDefinitions[0]);
    const [residentialUnits] = useAsyncState(() => getResidentialUnitsByProjectId(projectId), [projectId], null);
    const activeProfileId = useSelector((state: IState) => state.global.activeProfile!.id);
    const hasConnectedUnits = residentialUnits?.some(
        (x) => x.constructionPhaseId === (selectedConstructionPhaseId ? selectedConstructionPhaseId : null)
    );

    const project = useSelector((state: IState) => state.global.activeProject);

    const DEFAULT_CONSTRUCTION_PHASE_ID = 0;

    const constructionPhaseOptions = useMemo<ConstructionPhaseFilterItem[] | null>(() => {
        if (!constructionPhases) return null;
        return [{ id: DEFAULT_CONSTRUCTION_PHASE_ID, projectId: null, name: t`Default` }, ...constructionPhases];
    }, [constructionPhases]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const tabId = searchParams.get('tab') as SetupTabId;
        const constructionPhaseId = searchParams.get('constructionPhaseId');

        if (tabId) {
            const tabDefinition = setupTabDefinitions.find((tab) => tab.id === tabId);
            if (tabDefinition) setSelectedSetupTabDefinition(tabDefinition);
        }

        if (constructionPhaseId && constructionPhases) {
            const phase = constructionPhases.find((phase) => phase.id.toString() === constructionPhaseId);
            if (phase) {
                setSelectedConstructionPhaseId(phase.id);
            }
        } else if (!constructionPhaseId && constructionPhases) {
            setSelectedConstructionPhaseId(null);
        }
    }, [location.search, constructionPhases]);

    const setupProps = {
        projectId,
        constructionPhaseId: selectedConstructionPhaseId ?? null,
        isAdmin: project?.permissions.isAdministrator,
    };

    const onConstructionPhaseChange = (value: OnChangeValue<FilterItem, false>) => {
        const phaseId = value?.id === DEFAULT_CONSTRUCTION_PHASE_ID ? null : (value?.id ?? null);
        setSelectedConstructionPhaseId(phaseId);

        const searchParams = new URLSearchParams(location.search);
        const tabId = searchParams.get('tab') as SetupTabId;

        let newUrl = `/projects/${projectId}/landing-page`;
        const params = new URLSearchParams();

        if (phaseId != null) {
            params.append('constructionPhaseId', String(phaseId));
        }
        if (tabId) params.append('tab', tabId);
        if (params.toString()) newUrl += `?${params.toString()}`;

        history.push(newUrl);
    };

    const onSetupTabChange = (value: SetupTabDefinition) => {
        setSelectedSetupTabDefinition(value);

        const searchParams = new URLSearchParams(location.search);
        const constructionPhaseId = searchParams.get('constructionPhaseId');

        let newUrl = `/projects/${projectId}/landing-page`;
        const params = new URLSearchParams();

        if (constructionPhaseId) params.append('constructionPhaseId', constructionPhaseId);
        params.append('tab', value.id);
        newUrl += `?${params.toString()}`;

        history.push(newUrl);
    };

    if (!constructionPhases || !constructionPhaseOptions) return <LoadingIndicator />;

    // the defualt option donsnt come from the database, its "fabricated" by the frontend. if the selected con phase id is nullish, we use DEFAULT_CONSTRUCTION_PHASE_ID to find the default option.
    const dropdownValue = selectedConstructionPhaseId ?? DEFAULT_CONSTRUCTION_PHASE_ID;
    const selectedConstructionPhase = constructionPhaseOptions.find((x) => x.id === dropdownValue) ?? null;

    return (
        <div className={theme.layout}>
            {/* 16px = matches the Settings sidebar offset so the sticky gap is consistent. */}
            <Sticky className={theme.sidebar} additionalOffset={16}>
                <div className={theme.phaseFilter}>
                    <SingleFilterDropDown
                        onChange={onConstructionPhaseChange}
                        label={t`Select construction phase`}
                        placeholder={t`Select construction phase`}
                        data={constructionPhaseOptions}
                        value={selectedConstructionPhase ?? 0}
                    />
                </div>
                <Tabs
                    activeId={selectedSetupTabDefinition.id}
                    onChange={(id) => {
                        const tab = setupTabDefinitions.find((x) => x.id === id);
                        if (tab) onSetupTabChange(tab);
                    }}
                    orientation="vertical"
                    variant="pill"
                    align="stretch"
                    className={theme.tabs}>
                    {setupTabDefinitions.map((x) => (
                        <Tabs.Tab key={x.id} id={x.id} label={x.caption()} />
                    ))}
                </Tabs>
            </Sticky>
            <div className={theme.content}>
                {!hasConnectedUnits && (
                    <Banner type="warning">
                        <Trans>
                            This landing page is not connected to any building stages and will not be visible for any buyers
                            at the moment. Add a building stage to this landing page: Navigate to units and edit the relevant
                            unit group.
                        </Trans>
                    </Banner>
                )}
                <CoreApiProvider api={new CoreApi()} profileId={activeProfileId} projectId={projectId}>
                    <ProjectPermissionToggle condition="canEditLandingPage">
                        {selectedSetupTabDefinition.render(setupProps)}
                    </ProjectPermissionToggle>
                </CoreApiProvider>
            </div>
        </div>
    );
};

export default LandingPageSetup;
